import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getProfileSurvey,
  ProfileSurveyDTO,
} from 'src/api/dataVault/profileSurvey';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface Entry {
  status: Status;
  data: ProfileSurveyDTO | null;
  error: string | null;
  lastFetchedAt: number | null;
}

interface State {
  byTargetUserId: Record<number, Entry>;
}

const STALE_MS = 30_000;

const initialState: State = { byTargetUserId: {} };

export const fetchProfileSurvey = createAsyncThunk(
  'dataVaultProfileSurvey/fetch',
  async (
    {
      targetUserId,
      actingOnMemberId,
      forceRefresh = false,
    }: {
      targetUserId: number;
      actingOnMemberId?: number | null;
      forceRefresh?: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    const state = (getState() as { dataVaultProfileSurvey: State })
      .dataVaultProfileSurvey;
    const entry = state.byTargetUserId[targetUserId];
    if (
      !forceRefresh &&
      entry?.status === 'succeeded' &&
      entry.lastFetchedAt &&
      Date.now() - entry.lastFetchedAt < STALE_MS
    ) {
      return null; // cached; thunk aborts cleanly
    }
    try {
      return await getProfileSurvey({ actingOnMemberId });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load profile survey'
      );
    }
  }
);

const slice = createSlice({
  name: 'dataVaultProfileSurvey',
  initialState,
  reducers: {
    invalidateSubject(state, action: PayloadAction<number>) {
      delete state.byTargetUserId[action.payload];
    },
    clearEntity(state, action: PayloadAction<number>) {
      delete state.byTargetUserId[action.payload];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchProfileSurvey.pending, (state, action) => {
        const id = action.meta.arg.targetUserId;
        const forceRefresh = action.meta.arg.forceRefresh ?? false;
        const entry = state.byTargetUserId[id];
        // If data is still fresh and this isn't a forced refresh, the thunk body
        // will return null (cache hit) — don't clobber the existing succeeded entry.
        if (
          !forceRefresh &&
          entry?.status === 'succeeded' &&
          entry.lastFetchedAt &&
          Date.now() - entry.lastFetchedAt < STALE_MS
        ) {
          return;
        }
        // Preserve `data` from the previous succeeded fetch while we revalidate.
        // This is the stale-while-revalidate pattern: callers (e.g. the
        // ProfileSurveyCard CTA) can keep showing the cached survey id and
        // navigate immediately on click. Without this, the CTA flickers from
        // "Complete your profile" → "Loading…" → ready every time freshness
        // expires (>30s after the last fetch).
        state.byTargetUserId[id] = {
          status: 'loading',
          data: entry?.data ?? null,
          error: null,
          lastFetchedAt: entry?.lastFetchedAt ?? null,
        };
      })
      .addCase(fetchProfileSurvey.fulfilled, (state, action) => {
        const id = action.meta.arg.targetUserId;
        if (action.payload === null) {
          return;
        } // cached
        state.byTargetUserId[id] = {
          status: 'succeeded',
          data: action.payload,
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchProfileSurvey.rejected, (state, action) => {
        const id = action.meta.arg.targetUserId;
        state.byTargetUserId[id] = {
          status: 'failed',
          data: null,
          error: action.payload as string,
          lastFetchedAt: null,
        };
      });
  },
});

export const { invalidateSubject, clearEntity } = slice.actions;

export const selectProfileSurvey =
  (targetUserId: number) =>
  (state: { dataVaultProfileSurvey: State }): Entry | undefined =>
    state.dataVaultProfileSurvey.byTargetUserId[targetUserId];

export default slice.reducer;
